import { safeGetItem, safeSetItem } from "@/lib/safe-storage";
import { useEffect } from "react";

import { supabase } from "@/integrations/supabase/client";
import {
  PROFILE_KEY,
  notifyProfileChange,
  writeProfileLocal,
  type BusinessProfile,
} from "@/lib/persona";

export const NAV_STORAGE_KEY = "mypriorities:nav-primary";
export const NAV_EVENT = "mypriorities:nav-primary-change";
export const SECTION_EVENT = "mypriorities:section-order-change";
export const SECTION_HIDDEN_EVENT = "mypriorities:section-hidden-change";
export const MENU_POSITION_KEY = "mypriorities:menu-position";
export const MENU_POSITION_EVENT = "mypriorities:menu-position-change";

export function sectionStorageKey(pageKey: string) {
  return `mypriorities:section-order:${pageKey}`;
}

export function sectionHiddenStorageKey(pageKey: string) {
  return `mypriorities:section-hidden:${pageKey}`;
}

type RemotePrefs = {
  nav_primary: string[] | null;
  menu_position: string | null;
  section_order: Record<string, string[]> | null;
  section_hidden: Record<string, string[]> | null;
  business_profile: BusinessProfile | null;
};

async function loadRemote(): Promise<RemotePrefs | null> {
  const { data, error } = await supabase
    .from("settings")
    .select("nav_primary, menu_position, section_order, section_hidden, business_profile")
    .maybeSingle();
  if (error || !data) return null;
  return {
    nav_primary: Array.isArray(data.nav_primary) ? (data.nav_primary as string[]) : null,
    menu_position: typeof data.menu_position === "string" ? data.menu_position : null,
    section_order:
      data.section_order && typeof data.section_order === "object"
        ? (data.section_order as Record<string, string[]>)
        : null,
    section_hidden:
      data.section_hidden && typeof data.section_hidden === "object"
        ? (data.section_hidden as Record<string, string[]>)
        : null,
    business_profile:
      data.business_profile && typeof data.business_profile === "object"
        ? (data.business_profile as BusinessProfile)
        : null,
  };
}

async function saveRemote(patch: {
  nav_primary?: string[];
  menu_position?: string;
  section_order?: Record<string, string[]>;
  section_hidden?: Record<string, string[]>;
  business_profile?: BusinessProfile | null;
}) {
  const { data: auth } = await supabase.auth.getUser();
  const userId = auth.user?.id;
  if (!userId) return;
  const { data: existing } = await supabase
    .from("settings")
    .select("weekly_capacity, quarter_start_month")
    .maybeSingle();
  await supabase.from("settings").upsert(
    {
      user_id: userId,
      weekly_capacity: existing?.weekly_capacity ?? 40,
      quarter_start_month: existing?.quarter_start_month ?? 1,
      ...patch,
    },
    { onConflict: "user_id" },
  );
}

/** Save the onboarding answers locally and to the account. */
export function saveBusinessProfile(profile: BusinessProfile | null) {
  writeProfileLocal(profile);
  void saveRemote({ business_profile: profile }).catch(() => {});
}


/** Save the top bar order to the account so it follows the user to other devices. */
export function pushNavPrimary(order: string[]) {
  void saveRemote({ nav_primary: order }).catch(() => {});
}

/** Save where the menu sits (top, left, right, bottom) to the account. */
export function pushMenuPosition(position: string) {
  void saveRemote({ menu_position: position }).catch(() => {});
}

/** Save one page's section order to the account. */
export function pushSectionOrder(pageKey: string, order: string[] | null) {
  void (async () => {
    const remote = await loadRemote();
    const map = { ...(remote?.section_order ?? {}) };
    if (order) map[pageKey] = order;
    else delete map[pageKey];
    await saveRemote({ section_order: map });
  })().catch(() => {});
}

/** Save which sections one page hides to the account. */
export function pushSectionHidden(pageKey: string, hidden: string[]) {
  void (async () => {
    const remote = await loadRemote();
    const map = { ...(remote?.section_hidden ?? {}) };
    if (hidden.length) map[pageKey] = hidden;
    else delete map[pageKey];
    await saveRemote({ section_hidden: map });
  })().catch(() => {});
}

/**
 * Pulls the account's saved layout preferences into this browser once per session,
 * so a new device picks up the order chosen elsewhere.
 */
export function useLayoutPrefsSync() {
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const remote = await loadRemote();
      if (cancelled || !remote) return;

      if (remote.business_profile) {
        const next = JSON.stringify(remote.business_profile);
        if (safeGetItem(PROFILE_KEY) !== next) {
          safeSetItem(PROFILE_KEY, next);
          notifyProfileChange();
        }
      }



      if (remote.nav_primary) {
        const local = safeGetItem(NAV_STORAGE_KEY);
        const next = JSON.stringify(remote.nav_primary);
        if (local !== next) {
          safeSetItem(NAV_STORAGE_KEY, next);
          window.dispatchEvent(new Event(NAV_EVENT));
        }
      }

      if (remote.menu_position) {
        if (safeGetItem(MENU_POSITION_KEY) !== remote.menu_position) {
          safeSetItem(MENU_POSITION_KEY, remote.menu_position);
          window.dispatchEvent(new Event(MENU_POSITION_EVENT));
        }
      }

      let sectionsChanged = false;
      for (const [pageKey, order] of Object.entries(remote.section_order ?? {})) {
        if (!Array.isArray(order)) continue;
        const key = sectionStorageKey(pageKey);
        const next = JSON.stringify(order);
        if (safeGetItem(key) !== next) {
          safeSetItem(key, next);
          sectionsChanged = true;
        }
      }
      if (sectionsChanged) window.dispatchEvent(new Event(SECTION_EVENT));

      let hiddenChanged = false;
      for (const [pageKey, hidden] of Object.entries(remote.section_hidden ?? {})) {
        if (!Array.isArray(hidden)) continue;
        const key = sectionHiddenStorageKey(pageKey);
        const next = JSON.stringify(hidden);
        if (safeGetItem(key) !== next) {
          safeSetItem(key, next);
          hiddenChanged = true;
        }
      }
      if (hiddenChanged) window.dispatchEvent(new Event(SECTION_HIDDEN_EVENT));
    })();
    return () => {
      cancelled = true;
    };
  }, []);
}
